/* eslint-disable react-hooks/exhaustive-deps */
import { Link, Outlet, useParams } from 'react-router-dom';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { AuthContext, useAuth } from 'context/AuthContext';
import { useState, useEffect, useContext, useMemo, useRef } from 'react';

import './styles.scss';
import { PointItem } from 'components/point-item';
import ArticlePlaceholder from 'components/ui/article-placeholder';
import UserBadge from 'components/user-badge';
import { getGaleriaSecondByUser, getResultadosByUser, getSeloDestaque } from 'services/dashboard.service';
import TabTitle from 'components/tab-title';
import { getNamedDates } from 'services/utils';

export default function Selos() {
  const [culturaList, setCulturaList] = useState<any>();
  const [galeriaList, setGaleriaList] = useState<any>();
  const [realizacaoList, setRealizacaoList] = useState<any>();
  const [loadingCulturaList, setLoadingCulturaList] = useState(true);
  const [loadingGaleriaList, setLoadingGaleriaList] = useState(true);
  const [loadingRealizacaoList, setLoadingRealizacaoList] = useState(true);
  const [optionsCycle, setOptionsCycle] = useState<any>([]);
  const [activeItem, setActiveItem] = useState('');
  const [activeItemObj, setActiveItemObj] = useState<any>();

  const params = useParams();

  const { user } = useAuth();
  const { realCycle } = useContext(AuthContext);

  const isLoading = loadingCulturaList || loadingGaleriaList || loadingRealizacaoList;

  async function loadData() {
    if (!activeItem) return;
    setLoadingCulturaList(true);
    setLoadingGaleriaList(true);
    setLoadingRealizacaoList(true);

    async function loadCultura() {
      const culturaList = await getResultadosByUser(user?._id, activeItem);
      setCulturaList(culturaList);
      setLoadingCulturaList(false);
    }

    async function loadGaleria() {
      const galeriaList = await getGaleriaSecondByUser(user?._id, activeItem);
      setGaleriaList(galeriaList);
      setLoadingGaleriaList(false);
    }

    async function loadRealizacao() {
      const realizacaoList = await getSeloDestaque(user?._id, activeItem);
      setRealizacaoList(realizacaoList);
      setLoadingRealizacaoList(false);
    }

    loadCultura();
    loadGaleria();
    loadRealizacao();
  }

  useEffect(() => {
    if (!!user?._id) {
      loadData();
    }
  }, [user, activeItem]);

  const nextCycleValue = useMemo(() => {
    if (activeItemObj && activeItemObj.label) {
      const num = Number(activeItemObj.label.split('S')[0]);
      const year = Number(activeItemObj.label.split('S')[1]);

      if (num && year) {
        if (num === 1) {
          return `2S${year}`;
        } else {
          return `1S${year + 1}`;
        }
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [activeItemObj]);

  useEffect(() => {
    if (!realCycle) return;

    const cycleOptions = realCycle
      .filter((item: any) => item.date_start >= getNamedDates().ciclo_1S2023 && item.date_start <= getNamedDates().ciclo_1S2026)
      .map((cycle: any) => {
        cycle.label = 'Ciclo ' + cycle.title;
        cycle.value = cycle._id;

        return cycle;
      });

    setOptionsCycle(cycleOptions);
  }, [realCycle]);

  useEffect(() => {
    if (!optionsCycle?.[0]) return;

    if (!activeItem && optionsCycle?.length > 0) {
      setActiveItem(optionsCycle[0].value);
    }
  }, [optionsCycle]);

  const mainContentRef = useRef<HTMLDivElement>(null);

  // Ao trocar de ciclo/tab, move foco para o conteúdo principal
  const handleTabChange = (e: any) => {
    setActiveItem(e.value);
    setActiveItemObj(e);
    setTimeout(() => {
      if (mainContentRef.current) mainContentRef.current.focus();
    }, 0);
  };

  return (
    <>
      {!params.id ? (
        <main className="selo mx-auto container p-3 lg:p-10 overflow-hidden" aria-busy={isLoading}>
          <div className="mb-3 lg:mb-10">
            <nav className="flex flex-ro items-center space-x-3 lg:space-x-1" aria-label="Navegação da página">
              <Link
                to="../"
                className="rounded-full bg-purple flex justify-center items-center p-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow"
                aria-label="Voltar para página anterior"
              >
                <RiArrowLeftSLine size={28} className="text-white" aria-hidden="true" />
              </Link>

              <h1 className="text-lg lg:text-3xl py-1 px-6 rounded-full" tabIndex={0}>Galeria de selos</h1>
            </nav>
          </div>

          <div className="mb-5">
            <div className="space-y-5 lg:p-3">
              {optionsCycle && (
                <TabTitle
                  options={optionsCycle}
                  title={<p>Confira os selos que você ganhou no JogaJunto:</p>}
                  onCallBack={handleTabChange}
                />
              )}

              <aside className="lg:flex border-electric-blue border-2 rounded-2xl bg-oxford-blue py-5 px-7" aria-label="Informação importante">
                <div className="w-full border-electric-blue -my-5 py-5 pr-0 lg:pr-[70px] pl-3">
                  <p className="text-lg font-thin text-yellow" tabIndex={0}>
                    Informamos que o Selo Digital é emitido pela empresa <strong>Brasil Open Badge</strong> e em breve você receberá um
                    e-mail com o título <strong>"Ativar o seu Badge"</strong> do remetente <strong>"noreply@brasilopenbadge.com.br"</strong>.
                  </p>
                </div>
              </aside>

              <div
                className="flex flex-col lg:flex-row justify-between mb-5 mt-12"
                ref={mainContentRef}
                tabIndex={-1}
                aria-label="Conteúdo principal da galeria de selos"
              >
                <section className="w-full lg:w-[55%] lg:mr-8 mb-8 lg:mb-0" aria-labelledby="cultura-heading">
                  <div className="space-y-5">
                    <h2 id="cultura-heading" className="text-white font-medium text-[20px] mb-4" tabIndex={0}>Selo Destaques da Cultura</h2>

                    {!loadingCulturaList && (!culturaList || culturaList?.length === 0) && (
                      <div className="space-y-5 ring-2 ring-electric-blue px-10 py-8 rounded-[19px]" role="alert">
                        <p className="text-lg m-0 font-thin" tabIndex={0}>
                          Você não recebeu selo de Destaque da Cultura neste ciclo.
                          Não se preocupe, nos vemos no {nextCycleValue ? `ciclo ${nextCycleValue}` : 'próximo ciclo'}.
                          <span aria-hidden="true"> #AGenteTemOrgulhoDeVocê</span>
                          <span className="sr-only">A gente tem orgulho de você</span>
                        </p>
                      </div>
                    )}

                    {loadingCulturaList && (
                      <div role="status" aria-live="polite" aria-busy="true" className="space-y-2">
                        <span className="sr-only">Carregando selos de Destaque da Cultura</span>
                        <ArticlePlaceholder />
                      </div>
                    )}

                    {!loadingCulturaList && culturaList && culturaList?.length > 0 && (
                      <div className="lg:flex justify-between space-y-5 lg:space-y-0 lg:space-x-9 ring-2 ring-electric-blue px-10 py-8 rounded-[19px]">
                        <div className="space-y-2 w-fit lg:w-1/2">
                          <PointItem
                            data={{
                              items: [...culturaList],
                              type: 'destaque_cultura',
                            }}
                            cycleLabel={activeItemObj?.label}
                          />
                        </div>

                        <UserBadge badge={culturaList[0]} type="destaque_cultura" />
                      </div>
                    )}
                  </div>
                </section>

                <section className="w-full lg:w-[45%]" aria-labelledby="galeria-heading">
                  <div className="space-y-5">
                    <h2 id="galeria-heading" className="text-white font-medium text-[20px] mb-4" tabIndex={0}>Selo Galeria do Reconhecimento</h2>

                    {!loadingGaleriaList && (!galeriaList || galeriaList?.length === 0) && (
                      <div className="space-y-5 ring-2 ring-electric-blue px-10 py-8 rounded-[19px]" role="alert">
                        <p className="text-lg m-0 font-thin" tabIndex={0}>
                          Você não recebeu selo da Galeria do Reconhecimento neste ciclo.
                          Não se preocupe, nos vemos no {nextCycleValue ? `ciclo ${nextCycleValue}` : 'próximo ciclo'}.
                          <span aria-hidden="true"> #AGenteTemOrgulhoDeVocê</span>
                          <span className="sr-only">A gente tem orgulho de você</span>
                        </p>
                      </div>
                    )}

                    {loadingGaleriaList && (
                      <div role="status" aria-live="polite" aria-busy="true" className="space-y-2">
                        <span className="sr-only">Carregando selos da Galeria do Reconhecimento</span>
                        <ArticlePlaceholder />
                      </div>
                    )}

                    {galeriaList && galeriaList?.length > 0 && !loadingGaleriaList && (
                      <div className="lg:flex justify-between space-y-5 lg:space-y-0 lg:space-x-9 ring-2 ring-electric-blue px-10 py-8 rounded-[19px]">
                        <div className="space-y-2">
                          <PointItem
                            data={{
                              items: [...galeriaList],
                              type: 'galeria',
                            }}
                            cycleLabel={activeItemObj?.label}
                          />
                        </div>

                        <UserBadge badge={galeriaList[0]} type="galeria" />
                      </div>
                    )}
                  </div>
                </section>
              </div>

              <section className="w-full lg:w-[45%]" aria-labelledby="realizacao-heading">
                <div className="space-y-5">
                  <h2 id="realizacao-heading" className="text-white font-medium text-[20px] mb-4" tabIndex={0}>Selo Realização Destaque</h2>

                  {!loadingRealizacaoList && (!realizacaoList || realizacaoList?.length === 0) && (
                    <div className="space-y-5 ring-2 ring-electric-blue px-10 py-8 rounded-[19px]" role="alert">
                      <p className="text-lg m-0 font-thin" tabIndex={0}>
                        Você não recebeu selo de Realização Destaque neste ciclo.
                        Não se preocupe, nos vemos no {nextCycleValue ? `ciclo ${nextCycleValue}` : 'próximo ciclo'}.
                        <span aria-hidden="true"> #AGenteTemOrgulhoDeVocê</span>
                        <span className="sr-only">A gente tem orgulho de você</span>
                      </p>
                    </div>
                  )}

                  {loadingRealizacaoList && (
                    <div role="status" aria-live="polite" aria-busy="true" className="space-y-2">
                      <span className="sr-only">Carregando selos de Realização Destaque</span>
                      <ArticlePlaceholder />
                    </div>
                  )}

                  {realizacaoList && realizacaoList?.length > 0 && !loadingRealizacaoList && (
                    <div className="lg:flex justify-between space-y-5 lg:space-y-0 lg:space-x-9 ring-2 ring-electric-blue px-10 py-8 rounded-[19px]">
                      <div className="space-y-2">
                        <PointItem
                          data={{
                            items: [...realizacaoList],
                            type: 'selo_realizacao_destaque',
                          }}
                          cycleLabel={activeItemObj?.label}
                        />
                      </div>

                      <UserBadge badge={realizacaoList[0]} type="realizacao_destaque" />
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </main>
      ) : null}
      <Outlet />
    </>
  );
}
